import React, { useEffect, useState } from "react";
import { FileText, Eye, Download, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { fetchDocuments, documentViewUrl, documentDownloadUrl } from "@/api/documents";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

// /leden — bewust niet vindbaar (noindex, geen SSR, geen navigatie-link).
// Alleen bereikbaar via de gedeelde directe link.
export const LedenPage = () => {
  const [data, setData] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchDocuments();
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-testid="leden-page" className="bg-white min-h-[60vh]">
      <Seo title="Documenten voor leden — Lemmer Leeft" lang="nl" noindex />

      <section className="py-14 sm:py-20">
        <div className="ll-container max-w-3xl">
          <h1
            data-testid="leden-h1"
            className="text-[#173A5E] text-4xl sm:text-5xl font-extrabold leading-tight mb-4"
          >
            Documenten voor leden
          </h1>
          <p className="text-[#3E5871] font-[Inter] text-lg leading-relaxed mb-10">
            Hier vind je de documenten van BIZ Lemmer. Deze pagina is alleen
            bedoeld voor leden met de gedeelde link en is niet vindbaar via
            zoekmachines of het menu.
          </p>

          {loading && (
            <div className="flex items-center gap-2 text-[#5A7594] py-8">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Documenten laden…</span>
            </div>
          )}

          {error && !loading && (
            <p data-testid="leden-error" className="text-[#8b1420] py-8">
              De documenten konden niet worden geladen. Probeer het later opnieuw.
            </p>
          )}

          {!loading && !error && data.items.length === 0 && (
            <p data-testid="leden-empty" className="text-[#5A7594] py-8">
              Er zijn op dit moment geen documenten beschikbaar.
            </p>
          )}

          <ul data-testid="leden-document-list" className="space-y-4">
            {data.items.map((doc) => (
              <li
                key={doc.id}
                data-testid={`leden-document-${doc.id}`}
                className="bg-[#F4F8FB] border border-[#E4EEF5] rounded-2xl p-5 sm:p-6"
              >
                <div className="flex items-start gap-4">
                  <span className="shrink-0 w-11 h-11 rounded-xl bg-white border border-[#E4EEF5] flex items-center justify-center text-[#2E9CD6]">
                    <FileText className="w-5 h-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-[Poppins] font-bold text-[#173A5E] text-lg leading-snug">
                      {doc.title}
                    </h2>
                    {doc.doc_date && (
                      <p className="text-[#5A7594] text-sm mt-0.5">
                        {formatDate(doc.doc_date)}
                      </p>
                    )}
                    {doc.description && (
                      <p className="text-[#3E5871] font-[Inter] text-sm mt-2 leading-relaxed">
                        {doc.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-4">
                      <a
                        href={documentViewUrl(doc.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`leden-view-${doc.id}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm transition"
                      >
                        <Eye className="w-4 h-4" />
                        Bekijk
                      </a>
                      <a
                        href={documentDownloadUrl(doc.id)}
                        data-testid={`leden-download-${doc.id}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#173A5E] text-[#173A5E] hover:bg-[#173A5E] hover:text-white font-[Poppins] font-semibold text-sm transition"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </a>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default LedenPage;
